// ─────────────────────────────────────────────
//  OR Board A — the plain board, with a command strip above it.
//
//  One of three alternative boards. OR Board C puts everything on the room card
//  and adds no new global furniture; OR Board A leaves the card closer to the one
//  people already read and answers "what needs me" in a strip and a column.
//
//  THE IDEA BEING TESTED: the strip's counts are BUTTONS that filter the board
//  in place, so the answer to "which rooms are those?" is the same screen with
//  fewer cards on it — not a second screen, and not a list that names rooms you
//  then have to find. Press it again to clear.
//
//  Everything here reuses OR Board C's data, derivations, write paths and card.
//  The only thing being compared is the shape.
// ─────────────────────────────────────────────
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { logout } from "../firebase/auth";
import { useDayKey } from "../hooks/useDayKey";
import { useLiveConnection } from "../hooks/useLiveConnection";
import { useDeviceFacility } from "../hooks/useDeviceFacility";
import { useFacilities } from "../hooks/useFacilities";
import { useControlPanelData } from "../hooks/useControlPanelData";
import {
  assignDestinations,
  targetCaseFor,
  assignmentChangedSince,
  writesAllowed,
  WRITE_BLOCKED_COPY,
  stagedReliefRows,
  currentCrnaId,
  currentCrnaName,
} from "../domain/panelAssign";
import { nextBoundary } from "../domain/controlPanel";
import { placeProvider } from "../features/control-panel/placeProvider";
import { setRosterMemberDeparted, updateRoomStatus } from "../firebase/firestore";
import { confirmRelief } from "../features/relief/confirmRelief";
import { roomForOrRoom } from "../domain/gridTemplate";
import { groupRoomsByLocation } from "../domain/locations";
import { facilityLabel } from "../domain/facilities";
import NavMenu from "../components/NavMenu";
import SignOutButton from "../components/SignOutButton";
import BoardSwitcher from "../components/controlPanel/BoardSwitcher";
import PanelRoomCard from "../components/controlPanel/PanelRoomCard";
import PanelSheet from "../components/controlPanel/PanelSheet";
import FacilityPicker from "../components/controlPanel/FacilityPicker";
import BenchSections from "../components/controlPanel/BenchSections";
import styles, { sectionTint, C, MONO } from "../components/controlPanel/panelCard.styles";

const NOW_TICK_MS = 30_000;

function useNowMinutes() {
  const [nowMin, setNowMin] = useState(() => {
    const d = new Date();
    return d.getHours() * 60 + d.getMinutes();
  });
  useEffect(() => {
    const id = setInterval(() => {
      const d = new Date();
      setNowMin(d.getHours() * 60 + d.getMinutes());
    }, NOW_TICK_MS);
    return () => clearInterval(id);
  }, []);
  return nowMin;
}

// A count that filters. Pressed state is carried by aria-pressed AND a visible
// ring, never by colour alone.
function StripCard({ label, count, tone, glyph, active, onClick, disabled }) {
  const toneStyle =
    count > 0 && tone === "alarm" ? { background: C.alarmBg, borderColor: C.alarmLine, color: C.alarm }
      : count > 0 && tone === "warn" ? { background: C.warnBg, borderColor: C.warnLine, color: C.warn }
        : { background: C.card, borderColor: C.line, color: C.muted };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-disabled={disabled ? "true" : undefined}
      style={{
        flex: "1 1 160px",
        textAlign: "left",
        padding: "10px 12px",
        borderRadius: 10,
        border: "1px solid",
        cursor: disabled ? "default" : "pointer",
        outline: active ? `3px solid ${C.navy}` : "none",
        outlineOffset: 1,
        ...toneStyle,
      }}
    >
      <span style={{ display: "block", fontSize: 22, fontWeight: 800, fontFamily: MONO }}>
        <span aria-hidden="true" style={{ fontSize: 15 }}>{glyph} </span>
        {count}
      </span>
      <span style={{ display: "block", fontSize: 11.5, letterSpacing: "0.04em" }}>{label}</span>
    </button>
  );
}

export default function BoardAPage() {
  const { user, isAdmin, isAIC, isDoctor } = useAuth();
  const nowMin = useNowMinutes();
  const dateKey = useDayKey();
  const facilities = useFacilities();

  const device = useDeviceFacility({
    facilities,
    accountFacilityId: user?.activeFacilityId || null,
    allowedFacilityIds: user?.facilityIds,
    isAdmin,
  });
  const facilityId = device.facilityId;

  const breakWindows = useMemo(
    () => (facilities || []).find((f) => f && f.id === facilityId)?.breakWindows || null,
    [facilities, facilityId]
  );

  const {
    model, lanes, breakRowByRoomId, alarms, railMetrics, loading,
    assignments, rooms, available, locations, occupiedRooms,
    departedMembers, travelingMembers, mdCall, outstanding,
  } = useControlPanelData(facilityId, dateKey, { nowMin, breakWindows });

  const connection = useLiveConnection(facilityId);

  const isWallDisplay = user?.role === "boardwall";
  const canAct = Boolean(!isWallDisplay && (isAdmin || isAIC || isDoctor));

  const [filter, setFilter] = useState(null);   // null | emergency | uncovered | break
  const [sheetPerson, setSheetPerson] = useState(null);
  const [sheetLane, setSheetLane] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const destinations = useMemo(
    () => assignDestinations(lanes, sheetPerson?.id),
    [lanes, sheetPerson]
  );

  const reliefRows = useMemo(
    () => stagedReliefRows(assignments, { nowMin, resolveRoom: (o) => roomForOrRoom(o, rooms) }),
    [assignments, rooms, nowMin]
  );
  const reliefByRoomId = useMemo(() => {
    const m = new Map();
    for (const r of reliefRows) if (r.roomId) m.set(r.roomId, r);
    return m;
  }, [reliefRows]);

  const guard = useCallback(() => {
    const gate = writesAllowed({ canAct, connectionBand: connection.band });
    if (!gate.allowed) {
      setError(WRITE_BLOCKED_COPY[gate.reason] || "That is not allowed right now.");
      return false;
    }
    return true;
  }, [canAct, connection.band]);

  const commitPlacement = useCallback(
    async ({ person, lane: destLane, displaced, disposition, travelDest, travelDestName }) => {
      if (!guard()) return;
      const liveLane = lanes.find((l) => l.roomId === destLane.roomId) || null;
      const liveCase = targetCaseFor(liveLane);
      const conflict = assignmentChangedSince({ ahpId: displaced ? displaced.id : null }, liveCase);
      if (conflict) { setError(conflict); return; }
      setBusy(true);
      try {
        await placeProvider({
          person: person !== undefined ? person : sheetPerson,
          lane: liveLane || destLane,
          targetCase: liveCase,
          displaced, disposition, travelDest, travelDestName,
          facilityId, dateKey, actorUid: user?.uid,
        });
        setSheetPerson(null); setSheetLane(null); setError(null);
      } catch (e) {
        setError(e?.message || "That change could not be saved.");
      } finally { setBusy(false); }
    },
    [guard, lanes, sheetPerson, facilityId, dateKey, user]
  );

  const onConfirmRelief = useCallback(
    async (row) => {
      if (!guard()) return;
      setBusy(true);
      try { await confirmRelief(row.assignment, { roomId: row.roomId }); setError(null); }
      catch (e) { setError(e?.message || "That relief could not be confirmed."); }
      finally { setBusy(false); }
    },
    [guard]
  );

  // Send the person in a room home for the day. Marking them departed is what
  // takes them out of the staffing maths at every later boundary, so it is a
  // real change and not a note.
  const onSendHome = useCallback(
    async (lane) => {
      if (!guard()) return;
      const who = currentCrnaId(lane);
      if (!who) { setError(`${lane.roomName} has nobody to send home.`); return; }
      setBusy(true);
      try {
        await setRosterMemberDeparted(facilityId, dateKey, who, true, user?.uid);
        setError(null);
      } catch (e) { setError(e?.message || "That person could not be sent home."); }
      finally { setBusy(false); }
    },
    [guard, facilityId, dateKey, user]
  );

  const onStatus = useCallback(
    async (lane, status) => {
      if (!guard()) return;
      setBusy(true);
      try { await updateRoomStatus(lane.roomId, status); setError(null); }
      catch (e) { setError(e?.message || "That status could not be saved."); }
      finally { setBusy(false); }
    },
    [guard]
  );

  const boundary = useMemo(
    () => nextBoundary(model.staffing, nowMin, model.doctorStaffing),
    [model.staffing, model.doctorStaffing, nowMin]
  );

  const sections = useMemo(
    () => groupRoomsByLocation(occupiedRooms || [], locations || []),
    [occupiedRooms, locations]
  );
  const laneByRoomId = useMemo(() => {
    const m = new Map();
    for (const l of lanes) m.set(l.roomId, l);
    return m;
  }, [lanes]);

  // The filter narrows the SAME board rather than opening another screen. That
  // is the whole proposition of this layout.
  const matchesFilter = useCallback(
    (roomId) => {
      if (!filter) return true;
      if (filter === "emergency") return alarms.emergencyRoomIds?.has(roomId);
      if (filter === "uncovered") return alarms.uncoveredRoomIds?.has(roomId);
      if (filter === "break") return alarms.breakRequestRoomIds?.has(roomId);
      return true;
    },
    [filter, alarms]
  );

  const counts = {
    emergency: alarms.emergencyRoomIds?.size || 0,
    uncovered: alarms.uncoveredRoomIds?.size || 0,
    break: alarms.breakRequestRoomIds?.size || 0,
  };

  const label = facilityLabel(facilityId, facilities);

  if (!facilityId) {
    return (
      <div style={styles.page}>
        <FacilityPicker
          reason={device.reason}
          candidates={device.candidates}
          onChoose={device.choose}
          onRetry={device.retry}
          onSignOut={logout}
          busy={device.checking}
        />
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "8px 16px", background: C.card, borderBottom: `1px solid ${C.line}` }}>
        <NavMenu />
        <span style={{ fontWeight: 800, color: C.inkStrong }}>OR Board A</span>
        <BoardSwitcher compact />
        <span style={{ fontFamily: MONO, fontSize: 12, color: C.muted }}>
          {label} · {dateKey}
        </span>
        <span
          style={{
            ...styles.flag,
            ...(connection?.level === "live" ? styles.flagGood : styles.flagAlarm),
          }}
          data-testid="b3-connection"
          data-level={connection?.level}
        >
          <span aria-hidden="true">{connection?.glyph || "○"}</span>
          {connection?.rail || "CONNECTING…"}
        </span>
        {!canAct ? <span style={styles.flag}>READ ONLY</span> : null}
        {/* marginLeft:auto — this topbar is a single left-aligned row with no
            right-hand group, so the control claims the remaining space itself
            rather than every sibling growing a wrapper. */}
        <span style={{ marginLeft: "auto" }}><SignOutButton /></span>
      </div>

      {connection.band ? (
        <div role="alert" style={{ background: C.alarmBg, borderBottom: `2px solid ${C.alarm}`, color: "#7f1d1d", fontWeight: 800, padding: "8px 16px" }}>
          ▲ {connection.band}
        </div>
      ) : null}

      {error ? (
        <div style={{ background: C.warnBg, borderBottom: `1px solid ${C.warnLine}`, color: C.warn, fontWeight: 700, padding: "8px 16px", display: "flex", gap: 12 }}>
          <span>{error}</span>
          <button type="button" onClick={() => setError(null)} style={{ ...styles.act, marginLeft: "auto", minHeight: 26 }}>Dismiss</button>
        </div>
      ) : null}

      {/* THE COMMAND STRIP */}
      <div style={{ display: "flex", gap: 10, padding: "12px 16px 0", flexWrap: "wrap" }}>
        <StripCard
          label={filter === "emergency" ? "emergencies · showing" : "emergencies"}
          count={counts.emergency} tone="alarm" glyph="!"
          active={filter === "emergency"}
          disabled={!counts.emergency}
          onClick={() => counts.emergency && setFilter(filter === "emergency" ? null : "emergency")}
        />
        <StripCard
          label={filter === "uncovered" ? "uncovered · showing" : "rooms uncovered"}
          count={counts.uncovered} tone="warn" glyph="∅"
          active={filter === "uncovered"}
          disabled={!counts.uncovered}
          onClick={() => counts.uncovered && setFilter(filter === "uncovered" ? null : "uncovered")}
        />
        <StripCard
          label={filter === "break" ? "breaks waiting · showing" : "breaks waiting"}
          count={counts.break} tone="warn" glyph="●"
          active={filter === "break"}
          disabled={!counts.break}
          onClick={() => counts.break && setFilter(filter === "break" ? null : "break")}
        />
        {/* Not a filter: a forecast has no rooms to show yet. Dashed says so. */}
        <div
          style={{
            flex: "1 1 160px", padding: "10px 12px", borderRadius: 10,
            border: `1px dashed ${boundary && boundary.delta < 0 ? C.warnLine : C.line}`,
            background: boundary && boundary.delta < 0 ? C.warnBg : C.card,
            color: boundary && boundary.delta < 0 ? C.warn : C.muted,
          }}
        >
          <span style={{ display: "block", fontSize: 22, fontWeight: 800, fontFamily: MONO }}>
            <span aria-hidden="true" style={{ fontSize: 15 }}>▲ </span>
            {boundary ? (boundary.delta < 0 ? Math.abs(boundary.delta) : "ok") : "—"}
          </span>
          <span style={{ display: "block", fontSize: 11.5 }}>
            {boundary ? `${boundary.delta < 0 ? "short at" : "at"} ${boundary.label}` : "no boundary ahead"}
          </span>
        </div>
      </div>

      {filter ? (
        <div style={{ padding: "8px 16px 0" }}>
          <button type="button" onClick={() => setFilter(null)} style={{ ...styles.act }}>
            Showing only {filter === "break" ? "breaks waiting" : filter} · show all rooms
          </button>
        </div>
      ) : null}

      {sheetPerson || sheetLane ? (
        <div style={{ margin: "12px 16px", maxWidth: 520 }}>
          <PanelSheet
            person={sheetPerson}
            lane={sheetLane}
            bench={available?.providers || []}
            destinations={destinations}
            facilities={facilities}
            busy={busy}
            onCommit={commitPlacement}
            onClose={() => { setSheetPerson(null); setSheetLane(null); }}
          />
        </div>
      ) : null}

      <div style={{ display: "flex", alignItems: "flex-start", gap: 0 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          {loading ? (
            <p style={{ padding: "24px 16px", color: C.muted }}>Loading the day…</p>
          ) : (
            sections.map((sec) => {
              const shown = sec.rooms.filter((r) => matchesFilter(r.id));
              if (!shown.length) return null;
              return (
                <section key={sec.key} style={{ ...styles.section, background: sectionTint(sec.label) }}>
                  <h2 style={styles.sectionHead}>
                    {sec.label}
                    <span style={styles.countChip}>
                      {shown.length === sec.rooms.length ? shown.length : `${shown.length} of ${sec.rooms.length}`}
                    </span>
                  </h2>
                  <div style={styles.grid}>
                    {shown.map((room) => {
                      const lane = laneByRoomId.get(room.id) || {
                        roomId: room.id, roomName: room.name, sectionLabel: sec.label, cases: [],
                      };
                      return (
                        <PanelRoomCard
                          key={room.id}
                          lane={lane}
                          room={room}
                          axisStart={model.axisStart}
                          axisEnd={model.axisEnd}
                          nowMin={nowMin}
                          alarms={alarms}
                          breakRow={breakRowByRoomId.get(room.id)}
                          breakWindows={breakWindows}
                          reliefRow={reliefByRoomId.get(room.id)}
                          canAct={canAct}
                          onAssign={() =>
                            setSheetLane({
                              roomId: lane.roomId, roomName: lane.roomName,
                              holderId: currentCrnaId(lane), holderName: currentCrnaName(lane),
                            })
                          }
                          onEditTimes={() => setError("Times editing lives on OR Board C for now.")}
                          onRelieve={(l) => setError(`Staging relief for ${l.roomName} is next; Confirm already works when one is staged.`)}
                          onSendHome={(l) => onSendHome(l)}
                          onMessage={() => window.dispatchEvent(new CustomEvent("atc-open-messages"))}
                          onStatus={(l, st) => onStatus(l, st)}
                          onMoveRoom={(l) =>
                            setSheetLane({
                              roomId: l.roomId, roomName: l.roomName,
                              holderId: currentCrnaId(l), holderName: currentCrnaName(l),
                            })
                          }
                          onConfirmRelief={onConfirmRelief}
                          onCoverBreak={() => setError("Break lives on OR Board C for now.")}
                        />
                      );
                    })
                    }
                  </div>
                </section>
              );
            })
          )}

          <BenchSections
            bench={available?.providers || []}
            doctors={available?.doctors || []}
            departed={departedMembers}
            traveling={travelingMembers}
            canAct={canAct}
            onAssign={(person) => setSheetPerson(person)}
          />
        </div>

        {/* THE RIGHT COLUMN. Its designer's own doubt was whether this reads as
            helpful or as the rail that was rejected — which is exactly what
            running both boards on a real day is meant to answer. */}
        <aside style={{ width: 280, flex: "0 0 auto", padding: "14px 16px 14px 0", display: "grid", gap: 10 }}>
          <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: C.radius, padding: 12 }}>
            <h2 style={{ ...styles.sectionHead, padding: "0 0 8px" }}>Needs you<span style={styles.countChip}>{outstanding?.length || 0}</span></h2>
            {outstanding?.length ? (
              <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 8 }}>
                {outstanding.map((item) => (
                  <li key={item.key} style={{ fontSize: 12.5 }}>
                    <strong>{item.label}</strong>
                    {item.rooms?.length ? (
                      <span style={{ display: "block", color: C.muted, fontSize: 11.5 }}>{item.rooms.join(" · ")}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p style={{ margin: 0, fontSize: 12.5, color: C.muted }}>Nothing outstanding.</p>
            )}
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: C.radius, padding: 12 }}>
            <h2 style={{ ...styles.sectionHead, padding: "0 0 8px" }}>MD call<span style={styles.countChip}>{mdCall?.length || 0}</span></h2>
            <ul style={{ listStyle: "none", margin: 0, padding: 0, display: "grid", gap: 6 }}>
              {(mdCall || []).slice(0, 6).map((d, i) => (
                <li key={d.id || i} style={{ display: "flex", gap: 6, fontSize: 12.5 }}>
                  <span style={{ fontWeight: 700 }}>{d.name}</span>
                  {/* "L" on this list means LOCUM, never "last". */}
                  {d.isLocum ? <span style={{ ...styles.countChip, borderColor: C.navyLine, color: C.navy }}>Locum</span> : null}
                  {d.isAIC ? <span style={styles.countChip}>AIC</span> : null}
                </li>
              ))}
            </ul>
          </div>

          <div style={{ background: C.card, border: `1px solid ${C.line}`, borderRadius: C.radius, padding: 12, fontSize: 12.5, color: C.muted }}>
            <strong style={{ color: C.ink }}>{railMetrics.roomsRunning}</strong> of {railMetrics.roomsTotal} running ·{" "}
            <strong style={{ color: C.ink }}>{railMetrics.onShift}</strong> on shift ·{" "}
            <strong style={{ color: C.ink }}>{railMetrics.bench}</strong> free
          </div>
        </aside>
      </div>
    </div>
  );
}
